/**
 * HrAnnualHoursPrint — rendu imprimable de la page « Annual hours »
 * (hr-time-effective) : répartition annuelle des heures par collaborateur.
 *
 * Unité de contenu autonome (fetch propre + onDataReady), enveloppée dans
 * .ppl-print__section (A4 paysage, cf. PeoplePrint.css).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTimeEffective } from '../../../api/hrTimeEffective';
import { formatNumber } from '../../../utils/format';
import PrintReportHeader from './PrintReportHeader';

const f0 = (v) => formatNumber(Math.round(Number(v) || 0), 0);
const f1 = (v) => formatNumber(Number(v) || 0, 1);
// En-têtes sur 2 lignes (wrap) pour colonnes étroites.
const thW = { whiteSpace: 'normal', lineHeight: 1.12, verticalAlign: 'bottom' };

export default function HrAnnualHoursPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t: i18nT } = useTranslation();
  const tenantId = activeTenant?.id;
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    getTimeEffective(tenantId, year)
      .then(d => { if (d?.error) return; setData((d && d.data) ? d.data : d); })
      .catch(() => {})
      .finally(() => onDataReady && onDataReady('annual-hours'));
  }, [tenantId, year]); // eslint-disable-line

  const rows = useMemo(() => (data?.rows || []).map(r => {
    const tr = Number(r.tr_hours) || 0;
    return {
      ...r,
      person_name: `${(r.last_name || '').toUpperCase()} ${r.first_name || ''}`,
      fte_pct: r.fte != null
        ? Number(r.fte) * 100
        : (Number(r.activity_rate) || 0) * (Number(r.months_in_year) || 0) / 12 * 100,
      idle_pct: tr > 0 ? (Number(r.idle_hours) || 0) / tr * 100 : 0,
    };
  }).sort((a, b) => a.person_name.localeCompare(b.person_name)), [data]);

  const t = data?.totals || {};
  const idlePctTot = Number(t.tr_hours) > 0 ? (Number(t.idle_hours) || 0) / Number(t.tr_hours) * 100 : 0;

  return (
    <section className="ppl-print__section ppl-print__section--wide">
      <PrintReportHeader category="Time" title={i18nT('phrases:p_544722d684')} year={year} tenant={activeTenant} editedAt={editedAt} />

      <table className="ppl-print__table ppl-print__table--fixed ppl-print__table--dense" style={{ fontSize: '7.4pt' }}>
        <caption>Hours breakdown per person · {rows.length} persons</caption>
        <colgroup>
          <col style={{ width: '26mm' }} />{/* Person */}
          <col style={{ width: '12mm' }} />{/* FTE % */}
          <col style={{ width: '15mm' }} />{/* Total time */}
          <col style={{ width: '15mm' }} />{/* Holidays hours */}
          <col style={{ width: '16mm' }} />{/* Working hours */}
          <col style={{ width: '14mm' }} />{/* Corporate */}
          <col style={{ width: '15mm' }} />{/* Supervision */}
          <col style={{ width: '15mm' }} />{/* Absenteism */}
          <col style={{ width: '16mm' }} />{/* Production time */}
          <col style={{ width: '14mm' }} />{/* Billable */}
          <col style={{ width: '14mm' }} />{/* Non billable */}
          <col style={{ width: '13mm' }} />{/* Idle */}
          <col style={{ width: '12mm' }} />{/* Idle % */}
        </colgroup>
        <thead>
          <tr>
            <th style={thW}>Person</th>
            <th className="num" style={thW}>FTE %</th>
            <th className="num" style={thW}>Total Time (TT)</th>
            <th className="num" style={thW}>Holidays (hours)</th>
            <th className="num" style={thW}>Operational time (TO)</th>
            <th className="num" style={thW}>Corporate</th>
            <th className="num" style={thW}>Supervision</th>
            <th className="num" style={thW}>{i18nT('phrases:p_a5fc105a5b')}</th>
            <th className="num" style={thW}>Time required (TR / Capacity)</th>
            <th className="num" style={thW}>Billable hours</th>
            <th className="num" style={thW}>Non billable</th>
            <th className="num" style={thW}>Idle time</th>
            <th className="num" style={thW}>Idle %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={r.person_id} className={idx % 2 === 0 ? 'zebra' : ''}>
              <td><strong>{(r.last_name || '').toUpperCase()}</strong> {r.first_name || ''}</td>
              <td className="num nowrap">{f1(r.fte_pct)}%</td>
              <td className="num nowrap"><strong>{f0(r.tt_net)}</strong></td>
              <td className="num nowrap">{f0(r.holidays_hours)}</td>
              <td className="num nowrap"><strong>{f0(r.ot_hours)}</strong></td>
              <td className="num nowrap">{f0(r.corp_hours)}</td>
              <td className="num nowrap">{f0(r.team_mgmt_hours)}</td>
              <td className="num nowrap">{f0(r.special_leave_hours)}</td>
              <td className="num nowrap"><strong>{f0(r.tr_hours)}</strong></td>
              <td className="num nowrap">{f0(r.billable_hours)}</td>
              <td className="num nowrap">{f0(r.nonbillable_hours)}</td>
              <td className="num nowrap">{f0(r.idle_hours)}</td>
              <td className="num nowrap">{f0(r.idle_pct)}%</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="nowrap">TOTAL · {rows.length} persons</td>
            <td className="num nowrap" />
            <td className="num nowrap"><strong>{f0(t.tt_net)}</strong></td>
            <td className="num nowrap">{f0(t.holidays_hours)}</td>
            <td className="num nowrap"><strong>{f0(t.ot_hours)}</strong></td>
            <td className="num nowrap">{f0(t.corp_hours)}</td>
            <td className="num nowrap">{f0(t.team_mgmt_hours)}</td>
            <td className="num nowrap">{f0(t.special_leave_hours)}</td>
            <td className="num nowrap"><strong>{f0(t.tr_hours)}</strong></td>
            <td className="num nowrap">{f0(t.billable_hours)}</td>
            <td className="num nowrap">{f0(t.nonbillable_hours)}</td>
            <td className="num nowrap">{f0(t.idle_hours)}</td>
            <td className="num nowrap">{f0(idlePctTot)}%</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}
